import { ShowRoleType } from './show-role-type.model';

export class Show {
  id: number;
  name: string;
  imageUrl: string | null = null;

  /**
   * When true, this show uses the legacy hardcoded showreport form
   * instead of dynamic templates. Used for backward compatibility.
   */
  useLegacyShowreport: boolean = false;

  /**
   * Emoji identifier for the show, displayed in calendar events and UI.
   */
  emoji: string = '🎠';

  /**
   * Primary brand color for the show (hex format, e.g. '#FF5733').
   * Used for headers and accents in show-specific pages.
   */
  primaryColor: string | null = null;

  /**
   * Secondary brand color for the show (hex format).
   */
  secondaryColor: string | null = null;

  /**
   * Producer/venue name for the show, used in calendar event summaries.
   */
  producer: string = 'Efteling';

  /**
   * Default location address for performances of this show.
   * Used in calendar events when performance has no specific location.
   */
  defaultLocation: string = 'Efteling Theater, Europalaan 1, 5171 KW Kaatsheuvel, Nederland';

  /**
   * Role types defined for this show.
   * Loaded separately via the role types API, not included in standard show responses.
   */
  roleTypes: ShowRoleType[] = [];

  /**
   * When true, multiple performances on the same day are displayed as a single
   * card on the dashboard with all performance times listed.
   */
  aggregatePerformancesByDay: boolean = false;

  /**
   * When true, this show uses the Toneelmeester Logboek feature: the
   * "Toneelmeester Logboek" nav item and the per-performance log button
   * are visible. When false they're hidden.
   */
  hasLog: boolean = false;

  /**
   * When true, this show uses the daily Performer Fly inspection feature
   * — extra menu item, dashboard markers on performance cards, and
   * inspection PDFs attached to the showreport email. Default false; set
   * true for shows whose rig matches the MagicaLuna installation (the 13
   * inspection items are hardcoded for that rig).
   */
  hasPerformerFlyInspections: boolean = false;

  /**
   * When true, this show uses the rehearsal feature: the rehearsal-link
   * section (Genereer / Kopieer / WhatsApp / Invullen) is shown on each
   * future performance card, and any linked rehearsal content surfaces a
   * "Repetitie" button. Default false — opt-in per show; most productions
   * don't use this workflow.
   */
  hasRehearsals: boolean = false;

  // Per-show menu visibility flags. Each gates the corresponding navigation
  // entry in app.component.ts (`requiresShowFlag`). Default true so existing
  // shows keep every menu item visible until an admin disables one.
  hasTasks: boolean = true;
  hasMaintenance: boolean = true;
  hasPerformances: boolean = true;
  hasPlanner: boolean = true;
  hasShowreports: boolean = true;
  /**
   * Default recipient address for the showreport email — configured per
   * show in the admin show-edit form. The showreport-add page reads this
   * straight off the active show signal to pre-fill the "mail this report
   * to …" prompt; no separate API call needed.
   */
  showreportEmail?: string;
  hasStartupProcedure: boolean = true;
  hasShowTimer: boolean = true;
  hasProtocols: boolean = true;
  hasShowStopProcedure: boolean = true;
  hasEvacuationInstructions: boolean = true;
  hasContactList: boolean = false;
  hasSceneryChangeList: boolean = true;

  /**
   * Per-show toggles for non-menu UI elements. All three default OFF —
   * opt-in per show via the admin show-edit form.
   * - `hasCallsheetButton` / `hasEftelingShowreportButton` gate the
   *   download buttons on each performance card; most productions
   *   don't have these document types.
   * - `hasCalendarFeed` gates the personal-calendar-URL section on the
   *   settings page (an experimental personal-ICS subscription).
   */
  hasCallsheetButton: boolean = false;
  hasEftelingShowreportButton: boolean = false;
  hasCalendarFeed: boolean = false;

  /**
   * Creëer een nieuwe instantie van Show op basis van JSON-data
   */
  public createFromJson(json: any): Show {
    const instance = new Show();

    // Toewijzen van eigenschappen op basis van JSON
    instance.id = json.id ?? 0; // Fall-back naar 0 als id ontbreekt
    instance.name = json.name || ''; // Fall-back naar lege string als name ontbreekt
    instance.imageUrl = json.imageUrl || null;
    instance.useLegacyShowreport = json.useLegacyShowreport ?? false;
    instance.emoji = json.emoji ?? '🎠';
    instance.primaryColor = json.primaryColor ?? null;
    instance.secondaryColor = json.secondaryColor ?? null;
    instance.producer = json.producer ?? 'Efteling';
    instance.defaultLocation = json.defaultLocation ?? 'Efteling Theater, Europalaan 1, 5171 KW Kaatsheuvel, Nederland';
    instance.roleTypes = json.roleTypes ?? [];
    instance.aggregatePerformancesByDay = json.aggregatePerformancesByDay ?? false;
    instance.hasLog = json.hasLog ?? false;
    instance.hasPerformerFlyInspections = json.hasPerformerFlyInspections ?? false;
    instance.hasRehearsals = json.hasRehearsals ?? false;
    instance.hasTasks = json.hasTasks ?? true;
    instance.hasMaintenance = json.hasMaintenance ?? true;
    instance.hasPerformances = json.hasPerformances ?? true;
    instance.hasPlanner = json.hasPlanner ?? true;
    instance.hasShowreports = json.hasShowreports ?? true;
    instance.showreportEmail = json.showreportEmail ?? undefined;
    instance.hasStartupProcedure = json.hasStartupProcedure ?? true;
    instance.hasShowTimer = json.hasShowTimer ?? true;
    instance.hasProtocols = json.hasProtocols ?? true;
    instance.hasShowStopProcedure = json.hasShowStopProcedure ?? true;
    instance.hasContactList = json.hasContactList ?? false;
    instance.hasEvacuationInstructions = json.hasEvacuationInstructions ?? true;
    instance.hasSceneryChangeList = json.hasSceneryChangeList ?? true;
    instance.hasCallsheetButton = json.hasCallsheetButton ?? false;
    instance.hasEftelingShowreportButton = json.hasEftelingShowreportButton ?? false;
    instance.hasCalendarFeed = json.hasCalendarFeed ?? false;

    return instance;
  }
}
